import {
    FunctionComponent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { useVoucherTemplateFiles } from '../hooks/useVoucherTemplateFiles';

type PreviewDialogProps = {
    open: boolean;
    onClose: () => void;
    templateId: string | null;
};

export const PreviewDialog: FunctionComponent<PreviewDialogProps> = ({
    open,
    onClose,
    templateId,
}) => {
    const { fetchPreview } = useVoucherTemplateFiles();
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const urlRef = useRef<string | null>(null);

    const revokeUrl = useCallback(() => {
        if (urlRef.current) {
            URL.revokeObjectURL(urlRef.current);
            urlRef.current = null;
        }
    }, []);

    const loadPreview = useCallback(async () => {
        if (!templateId) return;
        revokeUrl();
        setLoading(true);
        const url = await fetchPreview(templateId);
        urlRef.current = url;
        setPreviewUrl(url);
        setLoading(false);
    }, [templateId, fetchPreview, revokeUrl]);

    useEffect(() => {
        if (open && templateId) {
            loadPreview();
        }
        return () => {
            revokeUrl();
            setPreviewUrl(null);
        };
    }, [open, templateId]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Vorschau</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 400,
                    }}
                >
                    {loading && <CircularProgress />}
                    {!loading && previewUrl && (
                        <img
                            src={previewUrl}
                            alt='Vorschau'
                            style={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain',
                            }}
                        />
                    )}
                    {!loading && !previewUrl && templateId && (
                        <Button onClick={loadPreview}>
                            Vorschau erneut laden
                        </Button>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};
